import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  type ExploreParams,
  type WhenFilter,
  type ContentTypeFilter,
  type SortOption,
  WHEN_LABELS,
  CONTENT_TYPE_LABELS,
  SORT_LABELS,
  defaultSortForMode,
} from "@/lib/exploreParams";
import type { ExploreMode } from "./ExploreModeToggle";

interface ExploreActiveFiltersProps {
  mode: ExploreMode;
  params: ExploreParams;
  nearMeActive?: boolean;
  favActive?: boolean;
  onRemove: (updates: Partial<ExploreParams>) => void;
  onRemoveNearMe?: () => void;
  onRemoveFav?: () => void;
  onClearAll: () => void;
}

interface FilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

/**
 * Chip bar showing active filters with individual remove and "Rensa alla".
 */
export function ExploreActiveFilters({
  mode,
  params,
  nearMeActive,
  favActive,
  onRemove,
  onRemoveNearMe,
  onRemoveFav,
  onClearAll,
}: ExploreActiveFiltersProps) {
  const chips: FilterChip[] = [];

  // "Nära mig" chip
  if (nearMeActive && onRemoveNearMe) {
    chips.push({
      key: "near",
      label: "Nära mig",
      onRemove: onRemoveNearMe,
    });
  }

  // "Favoriter" chip
  if (favActive && onRemoveFav) {
    chips.push({
      key: "fav",
      label: "Favoriter",
      onRemove: onRemoveFav,
    });
  }

  // "When" chip (only in "do" mode, only if not default)
  if (mode === "do" && params.when !== "upcoming") {
    chips.push({
      key: "when",
      label: WHEN_LABELS[params.when],
      onRemove: () => onRemove({ when: "upcoming" }),
    });
  }

  // Content type chips (only in "do" mode, only if not all selected)
  if (mode === "do") {
    const allTypes: ContentTypeFilter[] = ["events", "outings"];
    const isAllSelected =
      params.contentTypes.length === allTypes.length &&
      allTypes.every((t) => params.contentTypes.includes(t));

    if (!isAllSelected) {
      params.contentTypes.forEach((ct) => {
        chips.push({
          key: `type-${ct}`,
          label: CONTENT_TYPE_LABELS[ct],
          onRemove: () => {
            const next = params.contentTypes.filter((t) => t !== ct);
            if (next.length > 0) onRemove({ contentTypes: next });
            else onRemove({ contentTypes: allTypes }); // restore all if removing last
          },
        });
      });
    }
  }

  // Sort chip (only if not default for mode)
  if (params.sort !== defaultSortForMode(mode)) {
    chips.push({
      key: "sort",
      label: `Sorterat: ${SORT_LABELS[params.sort]}`,
      onRemove: () => onRemove({ sort: defaultSortForMode(mode) }),
    });
  }

  // Tag chips (places mode)
  if (mode === "places" && params.tags.length > 0) {
    params.tags.forEach((tag) => {
      chips.push({
        key: `tag-${tag}`,
        label: tag,
        onRemove: () => onRemove({ tags: params.tags.filter((t) => t !== tag) }),
      });
    });
  }

  if (chips.length === 0) return null;

  return (
    <div
      className="flex items-center gap-2 px-4 pb-2 overflow-x-auto scrollbar-none"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      {chips.map((chip) => (
        <button
          key={chip.key}
          onClick={chip.onRemove}
          className="shrink-0 inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-primary/10 text-primary text-xs font-medium transition-colors hover:bg-primary/20"
        >
          {chip.label}
          <X className="h-3 w-3" />
        </button>
      ))}
      {chips.length > 1 && (
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 h-7 text-xs text-muted-foreground"
          onClick={onClearAll}
        >
          Rensa alla
        </Button>
      )}
    </div>
  );
}
